/**
 * Capacitor on-device video compression bridge (v1).
 *
 * Large video uploads fail more often on slow/unreliable mobile connections
 * simply because the file is big. This module lets the native shell shrink a
 * picked video before it is handed to the uploader, using a native
 * `VideoCompressor` plugin that lives in the separate Capacitor shell repo
 * (not this one) — see docs/native/video_compressor_plugin.md for its
 * contract.
 *
 * This is a strict no-op everywhere else: outside the Capacitor shell, when
 * the native plugin is not registered, or when the `video_compression`
 * feature flag is off, `compressVideoBestEffort` returns the original file
 * untouched. Even when all three gates are open, compression is
 * best-effort only. It can NEVER make an upload worse than skipping it:
 * every failure mode (probe error, compress error, timeout, or a result
 * that fails post-compress validation) falls back to the original file. The
 * worst case is "no improvement," never a regression.
 *
 * Unlike push_registration.js and background_upload.js, there is no npm
 * package to dynamically import here — the native plugin has no published
 * JS wrapper (it is bundled only inside the native shell), so it is reached
 * the same way detection.js reaches window.Capacitor itself: through the
 * injected `window.Capacitor.Plugins.VideoCompressor` global. That means
 * there is nothing to lazy-load; the plugin reference is only ever read
 * behind isCapacitorEnvironment() + isCapacitorPluginAvailable(), so it is
 * never touched outside the shell.
 *
 * Mirrors the rest of the capacitor/ modules: guards up front, a single
 * `reportError -> Honeybadger.notify(..,
 * {context:{component:"capacitor_video_compress"}})`, and never throws out
 * to the caller.
 */

import Honeybadger from "@honeybadger-io/js";
import {
  isCapacitorEnvironment,
  isCapacitorPluginAvailable,
  capacitorPlatform,
} from "./detection";
import {
  SKIP_REASONS,
  COMPRESSION_TIMEOUT,
  raceWithTimeout,
  buildCompressionTelemetry,
  validationFailureReason,
  contentExemptionSkipReason,
} from "../utils/video_compression_policy";

// The native plugin's registerPlugin() name, not an npm package name — see
// the module doc comment above.
const VIDEO_COMPRESSOR_PLUGIN_NAME = "VideoCompressor";

const VIDEO_COMPRESSION_FLAG = "video_compression";

// v1 trigger-gate thresholds (docs/native/video_compressor_plugin.md and
// BUILD_SPEC.md carry the reasoning for each).
const BELOW_SIZE_THRESHOLD_BYTES = 50 * 1024 * 1024; // 50MB
const MIN_DURATION_MS = 5000; // 5s
const MAX_FPS_FOR_COMPRESSION = 60; // slow-mo / velocity capture is exempt above this
const MAX_DIMENSION = 1440; // cap the long edge; never upscale

// Hard timeout for the native compress() call. Generous because a large
// mezzanine-grade transcode can legitimately take a while, but bounded so a
// stuck native call can never hang the upload flow forever.
const COMPRESS_TIMEOUT_MS = 90000;

// targetBitrateFor() lookup. Two long-edge tiers (1440 / 1080-and-below), each
// split on whether the source frame rate is above 30fps (higher fps needs
// more bits per second to hold the same perceived quality).
const HIGH_FPS_THRESHOLD = 30;
const LOW_TIER_MAX_LONG_EDGE = 1080;
const TARGET_BITRATE_1440_HIGH_FPS_BPS = 24_000_000;
const TARGET_BITRATE_1440_STANDARD_BPS = 16_000_000;
const TARGET_BITRATE_1080_HIGH_FPS_BPS = 18_000_000;
const TARGET_BITRATE_1080_STANDARD_BPS = 11_000_000;

// The timeout sentinel and the race that produces it are shared with the
// WebCodecs compressor — see utils/video_compression_policy.js.
const COMPRESS_TIMEOUT = COMPRESSION_TIMEOUT;

/**
 * Mezzanine-grade target bitrate (bps) for a given output long edge and
 * source frame rate. Kept as a small, documented lookup rather than a
 * formula so the numbers can be tuned per tier without touching call sites.
 * @param {number} longEdge - output long edge in px (post max-dimension cap)
 * @param {number} fps
 * @returns {number} bps
 */
function targetBitrateFor(longEdge, fps) {
  const highFps = fps > HIGH_FPS_THRESHOLD;
  if (longEdge > LOW_TIER_MAX_LONG_EDGE) {
    return highFps
      ? TARGET_BITRATE_1440_HIGH_FPS_BPS
      : TARGET_BITRATE_1440_STANDARD_BPS;
  }
  return highFps
    ? TARGET_BITRATE_1080_HIGH_FPS_BPS
    : TARGET_BITRATE_1080_STANDARD_BPS;
}

/**
 * Whether the given feature flag is enabled for the current user. Rails sets
 * window._tkcEnabledFeatures in the layout (shared with the rest of the
 * Capacitor bridge and the main app).
 * @param {string} name
 * @returns {boolean}
 */
function flagOn(name) {
  try {
    return (
      typeof window !== "undefined" &&
      Array.isArray(window._tkcEnabledFeatures) &&
      window._tkcEnabledFeatures.includes(name)
    );
  } catch {
    return false;
  }
}

/**
 * Report a compression failure so it's visible in monitoring. Reporting must
 * never itself throw or block the caller.
 */
function reportError(error) {
  try {
    Honeybadger.notify(error, {
      context: { component: "capacitor_video_compress" },
    });
  } catch {
    // no-op
  }
}

/**
 * The native plugin reference, or null when unavailable. Read directly off
 * the injected Capacitor bridge (see module doc comment) rather than a
 * dynamic import, since there is no npm package to import here.
 * @returns {object|null}
 */
function getVideoCompressorPlugin() {
  try {
    const plugin =
      typeof window !== "undefined" &&
      window.Capacitor &&
      window.Capacitor.Plugins &&
      window.Capacitor.Plugins[VIDEO_COMPRESSOR_PLUGIN_NAME];
    return plugin || null;
  } catch {
    return null;
  }
}

/**
 * True only when compression could actually run: inside the Capacitor shell,
 * with the native VideoCompressor plugin registered, and the
 * `video_compression` feature flag on. All three guards live here so every
 * other function can rely on this single check.
 * @returns {boolean}
 */
export function isVideoCompressionAvailable() {
  try {
    return (
      isCapacitorEnvironment() &&
      isCapacitorPluginAvailable(VIDEO_COMPRESSOR_PLUGIN_NAME) &&
      flagOn(VIDEO_COMPRESSION_FLAG)
    );
  } catch {
    return false;
  }
}

/**
 * Build the flat telemetry object attached to the result. Always includes
 * compressed/skip_reason/elapsed_ms; adds src_* fields once a probe
 * succeeded, and out_* fields whenever a compress() call actually produced
 * an output (including the case where that output then failed post-compress
 * validation — those near-misses are exactly the "quality down" tripwire
 * this telemetry exists for). Never includes a raw file path.
 */
const buildTelemetry = buildCompressionTelemetry;

/**
 * Build a "return the original" result with telemetry attached.
 */
function skipResult({ fileUri, sizeBytes, skipReason, startedAt, probe, out }) {
  return {
    fileUri,
    sizeBytes,
    compressed: false,
    telemetry: buildTelemetry({
      compressed: false,
      skipReason,
      startedAt,
      probe,
      out,
    }),
  };
}

/**
 * v1 trigger gate. Returns the matching skip_reason string when the source
 * should not be compressed at all, or null when it's eligible.
 */
function triggerGateSkipReason({ sizeBytes, probe }) {
  // Size and duration are this compressor's own trigger — the WebCodecs one
  // fires on projected time in flight instead, so these two stay here rather
  // than in the shared policy module.
  if (sizeBytes <= BELOW_SIZE_THRESHOLD_BYTES) {
    return SKIP_REASONS.BELOW_SIZE_THRESHOLD;
  }
  if (probe.durationMs < MIN_DURATION_MS) {
    return SKIP_REASONS.TOO_SHORT;
  }
  // The content exemptions are shared. Note the target bitrate is measured at
  // MAX_DIMENSION and not at the source's own long edge: "already efficient"
  // means "already at or below what we would have encoded it to", and we would
  // have capped the long edge at MAX_DIMENSION.
  return contentExemptionSkipReason({
    probe,
    maxFps: MAX_FPS_FOR_COMPRESSION,
    maxDimension: MAX_DIMENSION,
    targetBitrateBps: targetBitrateFor(MAX_DIMENSION, probe.fps),
  });
}

/**
 * Best-effort cancel of an in-flight compress() call (e.g. after it timed
 * out). Never throws — this is a kill switch, not a guarantee.
 */
async function safeCancel(plugin) {
  try {
    if (typeof plugin.cancel === "function") {
      await plugin.cancel();
    }
  } catch {
    // best-effort only
  }
}

/**
 * Best-effort delete of a compressed temp file we're about to discard (e.g.
 * because it failed post-compress validation). Never throws — a leaked temp
 * file must never mask or block the caller's result.
 */
async function safeDeleteTemp(plugin, path) {
  if (!path || typeof plugin.deleteTemp !== "function") {
    return;
  }
  try {
    await plugin.deleteTemp({ path });
  } catch {
    // best-effort only
  }
}

/**
 * Public best-effort delete of a compressed temp file produced by
 * compressVideoBestEffort. The compressed output lives in the native
 * VideoCompressor plugin's OWN temp scheme, so it must be removed through the
 * plugin's deleteTemp, NOT the background uploader's releaseNativeTempFile
 * (which targets a different Capacitor Directory and would silently no-op,
 * leaking the file). Never throws; a no-op when compression is unavailable.
 */
export async function deleteCompressedTempFile(fileUri) {
  if (!fileUri) {
    return;
  }
  const plugin = getVideoCompressorPlugin();
  if (!plugin) {
    return;
  }
  await safeDeleteTemp(plugin, fileUri);
}

/**
 * Subscribe to native compress progress, if the plugin supports it. The
 * native contract does not mandate a progress event, so this is purely
 * best-effort: absence of addListener (or any failure to subscribe) just
 * means onProgress never fires, which is never itself a reason to fail
 * compression.
 * @returns {Promise<object|null>} a listener handle to remove later, or null
 */
async function wireProgressListener(plugin, onProgress) {
  if (
    typeof onProgress !== "function" ||
    typeof plugin.addListener !== "function"
  ) {
    return null;
  }
  try {
    return await plugin.addListener("progress", (event) => {
      try {
        onProgress(typeof event?.percent === "number" ? event.percent : event);
      } catch {
        // the caller's own progress handler must never break compression
      }
    });
  } catch {
    return null;
  }
}

async function removeProgressListener(handle) {
  try {
    await handle?.remove?.();
  } catch {
    // best-effort cleanup only
  }
}

/**
 * Race plugin.compress(params) against a hard timeout. Resolves to either
 * the compress() result or the COMPRESS_TIMEOUT sentinel. A synchronous
 * throw from plugin.compress is treated the same as a rejection (thrown).
 *
 * If the timeout wins the race, the compress promise is left running; a
 * `.catch(() => {})` safety net is attached up front so its eventual
 * rejection (if any) never surfaces as an unhandled promise rejection.
 */
async function compressWithTimeout(plugin, params, timeoutMs) {
  return raceWithTimeout(() => plugin.compress(params), timeoutMs);
}

/**
 * The actual compression pipeline, once the caller's arguments are in hand.
 * Every branch returns a result object (never throws); the one outer
 * try/catch in compressVideoBestEffort is a last-resort safety net for
 * anything unanticipated here.
 */
async function runCompression({ fileUri, sizeBytes, onProgress, startedAt }) {
  if (!isVideoCompressionAvailable()) {
    return skipResult({
      fileUri,
      sizeBytes,
      skipReason: "unavailable",
      startedAt,
    });
  }

  const plugin = getVideoCompressorPlugin();
  if (
    !plugin ||
    typeof plugin.probe !== "function" ||
    typeof plugin.compress !== "function"
  ) {
    return skipResult({
      fileUri,
      sizeBytes,
      skipReason: "unavailable",
      startedAt,
    });
  }

  let probe;
  try {
    probe = await plugin.probe({ inputPath: fileUri });
  } catch (error) {
    reportError(error);
    return skipResult({
      fileUri,
      sizeBytes,
      skipReason: "probe_failed",
      startedAt,
    });
  }

  const gateSkipReason = triggerGateSkipReason({ sizeBytes, probe });
  if (gateSkipReason) {
    return skipResult({
      fileUri,
      sizeBytes,
      skipReason: gateSkipReason,
      startedAt,
      probe,
    });
  }

  const srcLongEdge = Math.max(probe.widthPx, probe.heightPx);
  const outputLongEdge = Math.min(srcLongEdge, MAX_DIMENSION);
  const compressParams = {
    inputPath: fileUri,
    maxDimension: MAX_DIMENSION,
    targetBitrateBps: targetBitrateFor(outputLongEdge, probe.fps),
    preferredCodec: capacitorPlatform() === "ios" ? "hevc" : "h264",
    preserveFps: true,
    timeoutMs: COMPRESS_TIMEOUT_MS,
  };

  const progressHandle = await wireProgressListener(plugin, onProgress);
  let out;
  try {
    const raced = await compressWithTimeout(
      plugin,
      compressParams,
      COMPRESS_TIMEOUT_MS,
    );
    if (raced === COMPRESS_TIMEOUT) {
      await safeCancel(plugin);
      return skipResult({
        fileUri,
        sizeBytes,
        skipReason: "timeout",
        startedAt,
        probe,
      });
    }
    out = raced;
  } catch (error) {
    reportError(error);
    return skipResult({
      fileUri,
      sizeBytes,
      skipReason: "compress_failed",
      startedAt,
      probe,
    });
  } finally {
    await removeProgressListener(progressHandle);
  }

  const failureReason = validationFailureReason({ out, sizeBytes, probe });
  if (failureReason) {
    await safeDeleteTemp(plugin, out?.outputPath);
    return skipResult({
      fileUri,
      sizeBytes,
      skipReason: failureReason,
      startedAt,
      probe,
      out,
    });
  }

  return {
    fileUri: out.outputPath,
    sizeBytes: out.sizeBytes,
    compressed: true,
    telemetry: buildTelemetry({
      compressed: true,
      skipReason: null,
      startedAt,
      probe,
      out,
    }),
  };
}

/**
 * Compress a video on-device, best-effort. NEVER throws and NEVER blocks the
 * upload flow longer than the internal timeout: on any skip, failure, or
 * timeout this resolves to the ORIGINAL file with `telemetry.skip_reason`
 * set, so the caller can always just use whatever `fileUri`/`sizeBytes` it
 * gets back.
 *
 * @param {object} args
 * @param {string} args.fileUri - native `file://` path to the picked video.
 * @param {number} args.sizeBytes - size of the picked file, in bytes.
 * @param {(percent:number)=>void} [args.onProgress] - optional 0-100 progress
 *   callback; best-effort, may never fire (see wireProgressListener).
 * @returns {Promise<{fileUri:string, sizeBytes:number, compressed:boolean, telemetry:object}>}
 */
export async function compressVideoBestEffort(args) {
  const startedAt = Date.now();
  const fileUri = args?.fileUri;
  const sizeBytes = args?.sizeBytes;
  const onProgress = args?.onProgress;

  try {
    return await runCompression({ fileUri, sizeBytes, onProgress, startedAt });
  } catch (error) {
    reportError(error);
    return skipResult({
      fileUri,
      sizeBytes,
      skipReason: "error",
      startedAt,
    });
  }
}

export default {
  isVideoCompressionAvailable,
  compressVideoBestEffort,
  deleteCompressedTempFile,
};
